import { ProgressBar } from "@volumental/common";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../../../components/modal/modal";
import { useSurveyStore } from "../../../store";
import { SurveyStep } from "../../../store/survey";
import type { BrandStep, GenderStep, LengthStep, WidthStep } from "../../../store/survey";
import { BrandSelector } from "./brand-selector";
import { GenderAgeSelector } from "./gender-age-selector";
import { LengthSizeSelector } from "./length-size-selector";
import { WidthSizeSelector } from "./width-size-selector";

export function SelectorModal(): JSX.Element {
  const { survey, onBack, clearSurveyStore } = useSurveyStore((store) => store);
  const { t } = useTranslation();
  const onBackInModal = survey.step === SurveyStep.Brand ? undefined : onBack;
  const show = survey.step !== SurveyStep.Start && survey.step !== SurveyStep.Result;
  return (
    <Modal
      show={show}
      close={clearSurveyStore}
      modalTitle={t("modalTitles.surveySteps")}
      back={onBackInModal}
      disableScrollBar
    >
      {show ? <SelectorModalContent survey={survey} /> : undefined}
    </Modal>
  );
}

interface SelectorModalContentProps {
  survey: BrandStep | GenderStep | LengthStep | WidthStep;
}

function SelectorModalContent({ survey }: SelectorModalContentProps): JSX.Element {
  const { onNext } = useSurveyStore((store) => store);

  const activeSelector = (): JSX.Element => {
    switch (survey.step) {
      case SurveyStep.Brand: {
        return (
          <BrandSelector
            survey={survey}
            onClose={(brand) => {
              onNext({ brand });
            }}
          />
        );
      }
      case SurveyStep.Gender: {
        return (
          <GenderAgeSelector
            survey={survey}
            onClose={(gender) => {
              onNext({ gender });
            }}
          />
        );
      }
      case SurveyStep.Length: {
        return (
          <LengthSizeSelector
            survey={survey}
            onClose={(length, locale) => {
              onNext({ length, locale });
            }}
          />
        );
      }
      case SurveyStep.Width: {
        return (
          <WidthSizeSelector
            survey={survey}
            onClose={(width) => {
              onNext({ width });
            }}
          />
        );
      }
    }
  };

  const activeStep = (): 0 | 1 | 2 | 3 | -1 => {
    switch (survey.step) {
      case SurveyStep.Brand: {
        return 0;
      }
      case SurveyStep.Gender: {
        return 1;
      }
      case SurveyStep.Length: {
        return 2;
      }
      case SurveyStep.Width: {
        return 3;
      }
      default: {
        return -1;
      }
    }
  };

  return (
    <>
      <span className="pt-2 pb-4">
        <ProgressBar activeStep={activeStep()} stepsCount={4} />
      </span>
      {activeSelector()}
    </>
  );
}
